<template>
    <div class="member-directory">
        <h2>Member Directory</h2>
        <div class="search-container">
            <input 
                type="text" 
                v-model="searchQuery" 
                @input="filterMembers"
                placeholder="Search members..." 
                class="search-input"
            >
        </div>
        <div class="member-grid">
            <div v-for="member in filteredMembers" :key="member.id" class="member-card" @click="showMemberDetails(member)">
                <img :src="member.photo_url || DEFAULT_AVATAR" :alt="member.display_name" class="member-photo" @error="handleImageError">
                <div class="member-info">
                    <h3>{{ (member.first_name && member.last_name) ? `${member.first_name} ${member.last_name}` : member.display_name }}</h3>
                    <p class="company" v-if="member.company_name">{{ member.company_name }}</p>
                    <p class="title" v-if="member.title">{{ member.title }}</p>
                    <p class="email">{{ member.email }}</p>
                </div>
            </div>
        </div>

        <!-- Member Details Modal -->
        <div v-if="selectedMember" class="modal" @click.self="selectedMember = null">
            <div class="modal-content">
                <button class="close-btn" @click="selectedMember = null">&times;</button>
                <div class="member-details">
                    <!-- Add Share Actions -->
                    <div class="share-actions">
                        <button @click="shareContact(selectedMember)" class="share-btn">
                            <span class="material-icons">share</span>
                            Share
                        </button>
                        <button @click="copyToClipboard(selectedMember)" class="share-btn">
                            <span class="material-icons">content_copy</span>
                            Copy
                        </button>
                    </div>
                    <div class="member-header">
                        <button class="qr-code-btn" @click="generateQRCode" title="Generate Contact QR Code">
                            <span class="material-icons">qr_code</span>
                        </button>
                        <div class="photo-name-section">
                            <img :src="selectedMember.photo_url || DEFAULT_AVATAR" :alt="selectedMember.display_name" class="member-photo">
                            <h2>{{ (selectedMember.first_name && selectedMember.last_name) ? `${selectedMember.first_name} ${selectedMember.last_name}` : selectedMember.display_name }}</h2>
                        </div>
                    </div>

                    <!-- Company Info -->
                    <div class="member-details-section">
                        <div class="company-header">
                            <div class="company-details">
                                <h3>Company Information</h3>
                                <h4 v-if="selectedMember.company_name" class="company-name">{{ selectedMember.company_name }}</h4>
                                <p v-if="selectedMember.title" class="company-title">{{ selectedMember.title }}</p>
                                <p v-if="selectedMember.business_area" class="business-area">{{ selectedMember.business_area }}</p>
                                <a v-if="selectedMember.company_website" 
                                   :href="selectedMember.company_website" 
                                   target="_blank"
                                   rel="noopener noreferrer" 
                                   class="website-button">
                                    <span class="material-icons">language</span>
                                    Website
                                </a>
                            </div>
                            <img v-if="selectedMember.company_logo_url" 
                                 :src="selectedMember.company_logo_url" 
                                 :alt="selectedMember.company_name + ' logo'"
                                 class="company-logo"
                                 @error="handleLogoError"
                                 @load="handleLogoLoad">
                        </div>
                    </div>

                    <!-- Contact Information -->
                    <div class="member-details-section">
                        <div class="contact-header">
                            <h3>Contact Information</h3>
                            <a v-if="selectedMember.linkedin_url" 
                               :href="selectedMember.linkedin_url" 
                               target="_blank" 
                               rel="noopener noreferrer"
                               class="linkedin-button">
                                <img src="@/assets/linkedin-icon.svg" alt="LinkedIn" class="linkedin-icon">
                                View Profile
                            </a>
                        </div>
                        <div class="contact-details">
                            <div class="contact-row">
                                <span class="material-icons">email</span>
                                <a :href="'mailto:' + selectedMember.email">{{ selectedMember.email }}</a>
                            </div>
                            <div v-if="selectedMember.phone" class="contact-row">
                                <span class="material-icons">smartphone</span>
                                <a :href="'tel:' + selectedMember.phone">{{ formatPhoneNumber(selectedMember.phone) }}</a>
                            </div>
                        </div>
                    </div>

                    <!-- About Section -->
                    <div class="member-details-section">
                        <h3>About</h3>
                        <div class="about-content">
                            <div v-if="selectedMember.bio" class="about-person">
                                <h4>{{ selectedMember.first_name }}</h4>
                                <p>{{ selectedMember.bio }}</p>
                            </div>
                            <div v-if="selectedMember.company_bio" class="about-company">
                                <h4>{{ selectedMember.company_name }}</h4>
                                <p>{{ selectedMember.company_bio }}</p>
                            </div>
                        </div>
                    </div>

                    <!-- Referral Information -->
                    <div class="member-details-section" v-if="selectedMember.ideal_customer || selectedMember.avoid_referring">
                        <h3>Referral Information</h3>
                        <div class="referral-grid">
                            <div v-if="selectedMember.ideal_customer" class="referral-box ideal">
                                <h4>Ideal Referrals</h4>
                                <p>{{ selectedMember.ideal_customer }}</p>
                            </div>
                            <div v-if="selectedMember.avoid_referring" class="referral-box avoid">
                                <h4>Not Suitable For</h4>
                                <p>{{ selectedMember.avoid_referring }}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- QR Code Modal -->
        <div v-if="showQRModal" class="qr-modal-overlay" @click.self="closeQRModal">
            <div class="qr-modal-content">
                <h3>Contact QR Code</h3>
                <p>Scan this QR code to add {{ selectedMember.first_name }} to your contacts</p>
                <div class="qr-code-container">
                    <img v-if="qrCodeUrl" :src="qrCodeUrl" alt="Contact QR Code" />
                </div>
                <button class="close-btn" @click="closeQRModal">&times;</button>
            </div>
        </div>
    </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import defaultAvatar from '@/assets/default-avatar.png';
import { eventService, userService } from '../services/api';
import QRCode from 'qrcode';

export default {
    name: 'MemberDirectory',
    setup() {
        const members = ref([]);
        const filteredMembers = ref([]);
        const searchQuery = ref('');
        const loading = ref(true);
        const error = ref('');
        const selectedMember = ref(null);
        const DEFAULT_AVATAR = defaultAvatar;
        const showQRModal = ref(false);
        const qrCodeUrl = ref('');
        const currentUser = ref(null);

        const loadCurrentUser = async () => {
            try {
                const profile = await userService.getProfile();
                currentUser.value = profile;
            } catch (err) {
                console.error('Error loading current user:', err);
            }
        };

        const loadMembers = async () => {
            try {
                const response = await eventService.getAllMembers();
                members.value = response;
                filteredMembers.value = response;
                loading.value = false;
            } catch (err) {
                console.error('Error loading members:', err);
                error.value = 'Error loading members';
                loading.value = false;
            }
        };

        const filterMembers = () => {
            const query = searchQuery.value.toLowerCase();
            filteredMembers.value = members.value.filter(member => 
                member.first_name?.toLowerCase().includes(query) ||
                member.last_name?.toLowerCase().includes(query) ||
                member.company_name?.toLowerCase().includes(query) ||
                member.email?.toLowerCase().includes(query)
            );
        };

        const showMemberDetails = (member) => {
            selectedMember.value = member;
        };

        const formatPhoneNumber = (phone) => {
            if (!phone) return '';
            const match = phone.match(/^\+(\d{2})(\d{3})(\d{3})(\d{3})$/);
            if (match) {
                return `+${match[1]} ${match[2]} ${match[3]} ${match[4]}`;
            }
            return phone;
        };

        const handleImageError = (e) => {
            e.target.src = DEFAULT_AVATAR;
        };

        const handleLogoError = (e) => {
            e.target.style.display = 'none';
        };

        const handleLogoLoad = (e) => {
            e.target.style.display = 'block';
        };

        const handleImageLoad = () => {
            // Image loaded successfully
        };

        const generateVCardData = (member) => {
            const introducedBy = currentUser.value ? 
                `${currentUser.value.first_name} ${currentUser.value.last_name}` : 
                'Action Network Member';

            const vCardData = [
                'BEGIN:VCARD',
                'VERSION:3.0',
                `FN:${member.first_name} ${member.last_name}`,
                `N:${member.last_name};${member.first_name};;;`,
                member.email ? `EMAIL:${member.email}` : '',
                member.phone ? `TEL:${member.phone}` : '',
                member.company_name ? `ORG:${member.company_name}` : '',
                member.title ? `TITLE:${member.title}` : '',
                member.company_website ? `URL:${member.company_website}` : '',
                member.business_area ? 
                    `NOTE:Action Network [${member.business_area}] - Introduced by: ${introducedBy}` : 
                    `NOTE:Action Network - Introduced by: ${introducedBy}`,
                'END:VCARD'
            ].filter(line => line).join('\n');

            return vCardData;
        };

        const generateQRCode = async () => {
            if (!selectedMember.value) return;
            
            try {
                const vCardData = generateVCardData(selectedMember.value);
                const url = await QRCode.toDataURL(vCardData, {
                    errorCorrectionLevel: 'M',
                    margin: 4,
                    width: 300
                });
                qrCodeUrl.value = url;
                showQRModal.value = true;
            } catch (err) {
                console.error('Error generating QR code:', err);
            }
        };

        const closeQRModal = () => {
            showQRModal.value = false;
            qrCodeUrl.value = '';
        };

        const formatContactDetails = (member) => {
            const name = (member.first_name && member.last_name) ? `${member.first_name} ${member.last_name}` : member.display_name;
            return [
                name,
                member.company_name ? `Company: ${member.company_name}` : '',
                member.title ? `Title: ${member.title}` : '',
                member.business_area ? `Business Area: ${member.business_area}` : '',
                `Email: ${member.email}`,
                member.phone ? `Phone: ${formatPhoneNumber(member.phone)}` : '',
                member.company_website ? `Website: ${member.company_website}` : '',
                '\nAction Network Member Directory'
            ].filter(Boolean).join('\n');
        };

        const shareContact = async (member) => {
            const contactText = formatContactDetails(member);
            
            if (navigator.share) {
                try {
                    await navigator.share({
                        title: `Contact: ${member.display_name || `${member.first_name} ${member.last_name}`}`,
                        text: contactText,
                    });
                } catch (err) {
                    if (err.name !== 'AbortError') {
                        console.error('Share failed:', err);
                        copyToClipboard(member);
                    }
                }
            } else {
                copyToClipboard(member);
            }
        };

        const copyToClipboard = async (member) => {
            const contactText = formatContactDetails(member);
            
            try {
                // Try modern clipboard API first
                if (navigator.clipboard && navigator.clipboard.writeText) {
                    await navigator.clipboard.writeText(contactText);
                    alert('Contact details copied to clipboard');
                } else {
                    // Fallback for iOS Safari
                    const textArea = document.createElement('textarea');
                    textArea.value = contactText;
                    textArea.style.position = 'fixed';
                    textArea.style.left = '-9999px';
                    // Prevent zoom on iOS
                    textArea.style.fontSize = '16px';
                    textArea.style.userSelect = 'text';
                    textArea.setAttribute('readonly', '');
                    textArea.style.webkitUserSelect = 'text';
                    document.body.appendChild(textArea);
                    const range = document.createRange();
                    range.selectNodeContents(textArea);
                    const selection = window.getSelection();
                    selection.removeAllRanges();
                    selection.addRange(range);
                    
                    try {
                        document.execCommand('copy');
                        textArea.remove();
                        alert('Contact details copied to clipboard');
                    } catch (err) {
                        console.error('Fallback clipboard error:', err);
                        alert('Unable to copy contact details. Please try sharing instead.');
                    }
                }
            } catch (err) {
                console.error('Clipboard error:', err);
                alert('Unable to copy contact details. Please try sharing instead.');
            }
        };

        onMounted(() => {
            loadMembers();
            loadCurrentUser();
        });

        return {
            filteredMembers,
            searchQuery,
            loading,
            error,
            filterMembers,
            DEFAULT_AVATAR,
            selectedMember,
            showMemberDetails,
            formatPhoneNumber,
            handleImageError,
            handleLogoError,
            handleLogoLoad,
            handleImageLoad,
            showQRModal,
            qrCodeUrl,
            generateQRCode,
            closeQRModal,
            shareContact,
            copyToClipboard,
        };
    }
};
</script>

<style scoped>
.member-directory {
    padding: 2rem;
    max-width: 1200px;
    margin: 0 auto;
    width: 100%;
    box-sizing: border-box;
    overflow-x: hidden;
}

.search-container {
    margin-bottom: 2rem;
}

.search-input {
    width: 100%;
    max-width: 400px;
    padding: 0.75rem;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 1rem;
}

.member-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
    gap: 1.5rem;
}

.member-card {
    background: white;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    overflow: hidden;
    display: flex;
    padding: 1.25rem;
    cursor: pointer;
    transition: transform 0.2s, box-shadow 0.2s;
    align-items: flex-start;
}

.member-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
}

.member-photo {
    width: 70px;
    height: 70px;
    border-radius: 50%;
    object-fit: cover;
    margin-right: 1rem;
    flex-shrink: 0;
}

.member-info {
    flex: 1;
    text-align: left;
}

.member-info h3 {
    margin: 0 0 0.3rem 0;
    font-size: 1.1rem;
    color: #2c3e50;
}

.company {
    color: #666;
    font-size: 0.9rem;
    margin: 0 0 0.3rem 0;
}

.title {
    color: #666;
    font-size: 0.9rem;
    margin: 0 0 0.3rem 0;
}

.email {
    color: #666;
    font-size: 0.9rem;
    margin: 0;
    word-break: break-word;
    overflow-wrap: break-word;
}

.loading, .error, .no-results {
    text-align: center;
    padding: 2rem;
    color: #666;
}

.error {
    color: #dc3545;
}

@media (max-width: 768px) {
    .member-directory {
        padding-top: 80px;
        padding-left: 1rem;
        padding-right: 1rem;
        max-width: 100%;
    }

    .member-grid {
        grid-template-columns: 1fr;
        gap: 1rem;
        width: 100%;
    }

    .modal-content {
        width: 100%;
        max-width: calc(100% - 2rem);
        margin: 0 1rem;
        box-sizing: border-box;
    }
}

.title {
    font-weight: 500;
    color: #2c3e50;
    margin: 5px 0;
}

.company {
    color: #666;
    margin: 5px 0;
}

.business-area {
    color: #666;
    font-style: italic;
    margin: 5px 0;
}

.member-details-info {
    margin: 15px 0;
    text-align: center;
}

.contact-info {
    margin: 15px 0;
    padding: 15px;
    background: #f8f9fa;
    border-radius: 4px;
}

.bio {
    margin: 15px 0;
    padding: 15px;
    border-top: 1px solid #eee;
}

.bio h3 {
    margin-bottom: 10px;
    color: #2c3e50;
}

/* Modal Styles */
.modal {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
}

.modal-content {
    background: white;
    padding: 2.5rem 1.5rem 1.5rem 1.5rem;
    border-radius: 8px;
    max-width: 600px;
    width: 90%;
    position: relative;
    max-height: 90vh;
    overflow-y: auto;
}

.close-btn {
    position: absolute;
    top: 0.75rem;
    right: 1rem;
    background: none;
    border: none;
    font-size: 1.5rem;
    cursor: pointer;
    color: #666;
    padding: 0.5rem;
    z-index: 10;
}

.member-details {
    text-align: center;
}

.member-details .member-photo {
    width: 120px;
    height: 120px;
    margin: 0 auto 1rem auto;
}

.member-details h2 {
    margin-bottom: 1rem;
    color: #2c3e50;
}

.member-details-section {
    margin: 1.5rem 0;
    text-align: left;
    border-top: 1px solid #eee;
    padding-top: 1.5rem;
}

.member-details-section h3 {
    color: #2c3e50;
    font-size: 1.1rem;
    margin-bottom: 1rem;
}

.details-list {
    display: flex;
    flex-direction: column;
    gap: 1rem;
}

.detail-item {
    margin-bottom: 0.5rem;
}

.detail-item label {
    font-weight: 500;
    color: #666;
    font-size: 0.9rem;
    display: block;
    margin-bottom: 0.25rem;
}

.detail-item p {
    margin: 0;
    color: #2c3e50;
}

.detail-item a {
    color: #3498db;
    text-decoration: none;
}

.detail-item a:hover {
    text-decoration: underline;
}

.bio-text {
    line-height: 1.6;
    color: #2c3e50;
}

.member-header {
    position: relative;
    text-align: center;
    margin-bottom: 1.5rem;
}

.qr-code-btn {
    position: absolute;
    top: 0;
    left: 0;
    background: none;
    border: none;
    cursor: pointer;
    padding: 0.75rem;
    color: #666;
    z-index: 10;
}

.qr-code-btn .material-icons {
    font-size: 60px;
}

.close-btn {
    position: absolute;
    top: 0;
    right: 0;
    background: none;
    border: none;
    font-size: 1.5rem;
    cursor: pointer;
    color: #666;
    padding: 0.75rem;
    z-index: 10;
}

.photo-name-section {
    text-align: center;
    padding-top: 1rem;
}

.photo-name-section img {
    width: 100px;
    height: 100px;
    border-radius: 50%;
    margin-bottom: 0.5rem;
}

.photo-name-section h2 {
    margin: 0;
    color: #2c3e50;
    font-size: 1.4rem;
}

@media (max-width: 600px) {
    .qr-code-btn {
        padding: 0.75rem;
    }

    .qr-code-btn .material-icons {
        font-size: 60px;
    }

    .close-btn {
        padding: 0.75rem;
        font-size: 1.75rem;
    }
}

.qr-modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1001;
}

.qr-modal-content {
    background: white;
    padding: 2rem;
    border-radius: 8px;
    text-align: center;
    position: relative;
    max-width: 400px;
    width: 90%;
}

.qr-code-container {
    margin: 1rem 0;
    text-align: center;
}

.qr-code-container img {
    width: 200px;
    height: 200px;
    object-fit: cover;
}

.qr-code-btn {
    background: none;
    border: none;
    font-size: 1.5rem;
    cursor: pointer;
    color: #666;
    margin-left: 0.5rem;
}

.qr-code-btn:hover {
    color: #3498db;
}

.company-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 2rem;
    margin-bottom: 1rem;
}

.company-details {
    flex: 1;
}

.contact-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
}

.linkedin-button {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 1rem;
    background-color: #0077b5;
    color: white;
    border-radius: 4px;
    text-decoration: none;
    font-size: 0.9rem;
    transition: background-color 0.2s;
}

.linkedin-button:hover {
    background-color: #006097;
}

.linkedin-icon {
    width: 16px;
    height: 16px;
    filter: brightness(0) invert(1);
}

.contact-details {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.contact-details p {
    display: flex;
    align-items: center;
    gap: 10px;
    margin: 0;
}

.linkedin-icon {
    width: 20px;
    height: 20px;
}

.linkedin-link {
    color: #0077b5;
}

.linkedin-link a {
    color: #0077b5;
}

.referral-info {
    margin-top: 15px;
}

.referral-info h4 {
    color: #666;
    margin-bottom: 5px;
    font-size: 1rem;
}

.member-details-section {
    margin-bottom: 20px;
    padding: 15px;
    background: #f9f9f9;
    border-radius: 4px;
}

.member-details-section h3 {
    margin: 0 0 10px 0;
    color: #444;
    font-size: 1.2rem;
}

.member-details-section p {
    margin: 5px 0;
    color: #666;
    line-height: 1.5;
}

.company-logo {
    width: 120px;
    height: 80px;
    object-fit: contain;
    background: white;
    padding: 8px;
    border-radius: 4px;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.linkedin-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.linkedin-icon {
    width: 20px;
    height: 20px;
}

.linkedin-item a {
    color: #0077b5;
    text-decoration: none;
}

.linkedin-item a:hover {
    text-decoration: underline;
}

.contact-details p {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin: 0;
    color: #666;
}

.contact-details a {
    color: #2c3e50;
    text-decoration: none;
}

.contact-details a:hover {
    text-decoration: underline;
}

.member-details-section {
    background: #f8f9fa;
    border-radius: 8px;
    padding: 1.5rem;
    margin-bottom: 1.5rem;
}

.company-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 1rem;
    margin-bottom: 1rem;
}

.company-details {
    flex: 1;
}

.contact-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
}

.linkedin-button {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 1rem;
    background-color: #0077b5;
    color: white;
    border-radius: 4px;
    text-decoration: none;
    font-size: 0.9rem;
    transition: background-color 0.2s;
}

.linkedin-button:hover {
    background-color: #006097;
}

.linkedin-icon {
    width: 16px;
    height: 16px;
    filter: brightness(0) invert(1);
}

.contact-details {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
}

.contact-row {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    color: #666;
    word-break: break-word;
overflow-wrap: break-word;
}

.contact-row .material-icons {
    font-size: 20px;
    color: #4285f4;
}

.contact-row a {
    color: #2c3e50;
    text-decoration: none;
}

.contact-row a:hover {
    text-decoration: underline;
}

.member-details-section {
    background: #f8f9fa;
    border-radius: 8px;
    padding: 1.25rem;
    margin-bottom: 1rem;
}

.member-details-section h3 {
    color: #2c3e50;
    font-size: 1.1rem;
    margin: 0;
}

.about-content {
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
}

.about-person, .about-company {
    background: white;
    padding: 1rem;
    border-radius: 4px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.05);
}

.about-content h4 {
    color: #2c3e50;
    margin: 0 0 0.5rem 0;
    font-size: 1rem;
}

.about-content p {
    color: #666;
    margin: 0;
    line-height: 1.5;
}

.referral-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
    margin-top: 0.5rem;
}

.referral-box {
    background: white;
    padding: 1rem;
    border-radius: 4px;
    box-shadow: 0 1px 3px rgba(0,0,0,0.05);
}

.referral-box h4 {
    color: #2c3e50;
    margin: 0 0 0.5rem 0;
    font-size: 0.9rem;
    font-weight: 600;
}

.referral-box p {
    color: #666;
    margin: 0;
    font-size: 0.9rem;
    line-height: 1.4;
}

.ideal {
    border-left: 3px solid #4CAF50;
}

.avoid {
    border-left: 3px solid #f44336;
}

@media (max-width: 600px) {
    .referral-grid {
        grid-template-columns: 1fr;
    }
}

@media (max-width: 768px) {
    .directory-container {
        padding-top: 80px;
    }

    .member-grid {
        grid-template-columns: 1fr;
        gap: 1rem;
    }
}

.share-actions {
    display: flex;
    justify-content: center;
    gap: 1rem;
    margin: 1rem 0;
}

.share-btn {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 1rem;
    border: none;
    border-radius: 4px;
    background: #f0f4f8;
    color: #2c3e50;
    cursor: pointer;
    transition: background-color 0.2s;
    min-width: 100px;
}

.share-btn:hover {
    background: #e3e8ed;
}

.share-btn .material-icons {
    font-size: 1.2rem;
}

@media (max-width: 768px) {
    .share-actions {
        padding: 0 1rem;
        gap: 0.5rem;
    }

    .share-btn {
        flex: 1;
        justify-content: center;
        padding: 0.75rem 0.5rem;
    }
}

@media (max-width: 768px) {
    .modal-content {
        padding-top: 3rem;
    }

    .close-btn {
        top: 1rem;
        right: 0.75rem;
    }
}

.website-button {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 1rem;
    background-color: #2196F3;
    color: white;
    border: none;
    border-radius: 4px;
    text-decoration: none;
    font-size: 0.9rem;
    font-weight: 500;
    cursor: pointer;
    transition: background-color 0.2s;
    margin-top: 0.5rem;
}

.website-button:hover {
    background-color: #1976D2;
    text-decoration: none;
}

.website-button .material-icons {
    font-size: 18px;
}
</style>